import { useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useListPtoChecklistsQuery,
  useCreatePtoChecklistMutation,
  useListPtoChecklistTemplatesQuery,
  useCreatePtoChecklistFromTemplateMutation,
  useGetPtoChecklistQuery,
  useSetPtoChecklistItemStatusMutation,
  useLazyExportPtoCompletenessXlsxQuery,
  type PtoChecklist as PtoChecklistType,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 380px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font: inherit;
`;

const TextArea = styled.textarea`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font: inherit;
  min-height: 120px;
  resize: vertical;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ClickRow = styled.div<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
`;

const Bar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
  margin: ${({ theme }) => theme.spacing(2)} 0;
`;

const Fill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ theme }) => theme.colors.primary};
`;

const ITEM_STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает',
  present: 'В наличии',
  missing: 'Отсутствует',
  n_a: 'Не требуется',
};

/** Детализация одного чек-листа: пункты, готовность, отметка наличия, XLSX. */
function ChecklistDetailView({
  checklistId,
  canWrite,
}: {
  checklistId: string;
  canWrite: boolean;
}) {
  const { data, isLoading } = useGetPtoChecklistQuery(checklistId);
  const [setItemStatus] = useSetPtoChecklistItemStatusMutation();
  const [exportXlsx, { isFetching: exporting }] = useLazyExportPtoCompletenessXlsxQuery();

  if (isLoading) return <Muted>Загрузка…</Muted>;
  const detail = data?.detail;
  if (!detail) return <Muted>Чек-лист не найден.</Muted>;

  async function onExport() {
    try {
      const blob = await exportXlsx(checklistId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pto_completeness_${checklistId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }

  return (
    <Panel>
      <Row>
        <div>
          <strong>{detail.checklist.title}</strong>
          <Muted style={{ fontSize: 12 }}>{detail.checklist.object_type ?? 'тип не указан'}</Muted>
        </div>
        <Button $variant="primary" $size="sm" disabled={exporting} onClick={onExport}>
          {exporting ? 'Формирование…' : 'Реестр XLSX'}
        </Button>
      </Row>

      <Muted>
        Готовность: <strong>{detail.completeness_pct}%</strong> ({detail.present_count} из{' '}
        {detail.total_required} обязательных){detail.ready ? ' · комплект собран' : ''}
      </Muted>
      <Bar>
        <Fill $pct={detail.completeness_pct} />
      </Bar>

      {detail.items.length === 0 ? (
        <Muted>Пунктов пока нет.</Muted>
      ) : (
        detail.items.map((it) => (
          <ItemRow key={it.id}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {it.name}
                {it.code ? ` (${it.code})` : ''}
                {!it.required ? ' · необязательный' : ''}
              </div>
              <Muted style={{ fontSize: 12 }}>
                {ITEM_STATUS_LABEL[it.status] ?? it.status}
                {it.valid_until ? ` · действует до ${it.valid_until}` : ''}
                {it.needs_review ? ' · требует проверки' : ''}
              </Muted>
            </div>
            {canWrite && (
              <div style={{ display: 'flex', gap: 6 }}>
                <Button
                  $variant="ghost"
                  $size="sm"
                  onClick={() => setItemStatus({ id: it.id, status: 'present' })}
                >
                  В наличии
                </Button>
                <Button
                  $variant="ghost"
                  $size="sm"
                  onClick={() => setItemStatus({ id: it.id, status: 'missing' })}
                >
                  Нет
                </Button>
                <Button
                  $variant="ghost"
                  $size="sm"
                  onClick={() => setItemStatus({ id: it.id, status: 'n_a' })}
                >
                  Н/Д
                </Button>
              </div>
            )}
          </ItemRow>
        ))
      )}
    </Panel>
  );
}

export function PtoChecklist() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data, isLoading } = useListPtoChecklistsQuery(projectId, { skip: !projectId });
  const [create, { isLoading: saving }] = useCreatePtoChecklistMutation();
  const { data: tplData } = useListPtoChecklistTemplatesQuery();
  const [createFromTpl, { isLoading: creatingTpl }] = useCreatePtoChecklistFromTemplateMutation();

  const [title, setTitle] = useState('');
  const [objectType, setObjectType] = useState('');
  const [itemsText, setItemsText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [tplKey, setTplKey] = useState('');

  const checklists: PtoChecklistType[] = data?.items ?? [];
  const templates = tplData?.items ?? [];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Укажите название чек-листа.');
      return;
    }
    const items = itemsText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .map((name) => ({ name }));
    try {
      const res = await create({
        projectId,
        title: title.trim(),
        object_type: objectType.trim() || undefined,
        items: items.length > 0 ? items : undefined,
      }).unwrap();
      setTitle('');
      setObjectType('');
      setItemsText('');
      setSelected(res.checklist.id);
    } catch {
      setError('Не удалось создать чек-лист.');
    }
  }

  async function onCreateFromTemplate() {
    setError(null);
    const key = tplKey || templates[0]?.key;
    if (!key) return;
    try {
      const res = await createFromTpl({ projectId, template_key: key }).unwrap();
      setSelected(res.checklist.id);
    } catch {
      setError('Не удалось создать чек-лист из перечня.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Контроль комплектности ИРД</h1>
          <Muted>Чек-листы исходно-разрешительной документации · сроки действия</Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      <Columns>
        <div>
          {can_write && templates.length > 0 && (
            <>
              <h3 style={{ marginTop: 0 }}>Из стандартного перечня</h3>
              <Panel style={{ marginBottom: 16 }}>
                <Field>
                  Типовой перечень ИРД
                  <Select value={tplKey} onChange={(e) => setTplKey(e.target.value)}>
                    {templates.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.title} — {t.item_count} поз.
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button $variant="primary" disabled={creatingTpl} onClick={onCreateFromTemplate}>
                  {creatingTpl ? 'Создание…' : 'Создать из перечня'}
                </Button>
                <Muted style={{ fontSize: 12, marginTop: 8 }}>
                  Номенклатура с НТД-ссылками; наличие и сроки заполняются по факту.
                </Muted>
              </Panel>
            </>
          )}
          <h3 style={{ marginTop: 0 }}>Новый чек-лист</h3>
          {can_write ? (
            <Panel as="form" onSubmit={onSubmit}>
              <Field>
                Название
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Комплектность ИРД по объекту"
                />
              </Field>
              <Field>
                Тип объекта (опц.)
                <Input
                  value={objectType}
                  onChange={(e) => setObjectType(e.target.value)}
                  placeholder="Площадочный СМР+ПНР"
                />
              </Field>
              <Field>
                Пункты — по одному в строке (опц.)
                <TextArea
                  value={itemsText}
                  onChange={(e) => setItemsText(e.target.value)}
                  placeholder={'Разрешение на строительство\nГПЗУ\nТУ на подключение'}
                />
              </Field>
              <Button $variant="primary" type="submit" disabled={saving || !title.trim()}>
                {saving ? 'Создание…' : 'Создать чек-лист'}
              </Button>
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          ) : (
            <Muted>Недостаточно прав для создания чек-листов.</Muted>
          )}
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>Чек-листы проекта</h3>
          {isLoading ? (
            <Muted>Загрузка…</Muted>
          ) : checklists.length === 0 ? (
            <Muted>Чек-листов пока нет.</Muted>
          ) : (
            <>
              {checklists.map((c) => (
                <ClickRow
                  key={c.id}
                  $active={selected === c.id}
                  onClick={() => setSelected(selected === c.id ? null : c.id)}
                >
                  <div style={{ fontWeight: 600 }}>{c.title}</div>
                  <Muted style={{ fontSize: 12 }}>
                    {c.object_type ?? 'тип не указан'} · статус: {c.status}
                  </Muted>
                </ClickRow>
              ))}
              {selected && (
                <div style={{ marginTop: 12 }}>
                  <ChecklistDetailView checklistId={selected} canWrite={can_write} />
                </div>
              )}
            </>
          )}
        </div>
      </Columns>
    </div>
  );
}
